import { useState, type FormEvent, type ChangeEvent } from 'react';
import { motion } from 'framer-motion';
import { Send, Mail, MessageSquare, Building2, ArrowRight, type LucideIcon } from 'lucide-react';

interface FormState {
  name: string;
  email: string;
  company: string;
  topic: string;
  message: string;
}

interface ContactMethod {
  icon: LucideIcon;
  label: string;
  value: string;
}

const topics: string[] = [
  'Cloud Infrastructure',
  'AI Consulting',
  'Workflow Automation',
  'AWS Migration',
  'Cost Optimization',
  'Other',
];

const contactMethods: ContactMethod[] = [
  { icon: Mail, label: 'Email', value: 'hello@mataly.cloud' },
  { icon: MessageSquare, label: 'Response Time', value: 'Within 24 hours' },
  { icon: Building2, label: 'Engagement', value: 'Project & Retainer models' },
];

const initialForm: FormState = {
  name: '',
  email: '',
  company: '',
  topic: '',
  message: '',
};

export default function Contact() {
  const [form, setForm] = useState<FormState>(initialForm);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <section id="contact" className="relative py-24 lg:py-32 bg-black overflow-hidden">
      <div className="divider-glow mb-0" />

      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-blue-600/[0.05] blur-[120px] rounded-full pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-xs font-medium text-blue-400 uppercase tracking-widest mb-3">Get in Touch</p>
            <h2 className="text-4xl sm:text-5xl font-black text-white tracking-tight leading-tight mb-6">
              Ready to build
              <br />
              <span className="gradient-text">something remarkable?</span>
            </h2>
            <p className="text-slate-400 text-base leading-relaxed mb-10">
              Whether you need to migrate to AWS, build AI pipelines, or automate manual workflows—
              let's talk. First consultation is always free.
            </p>

            <div className="flex flex-col gap-4">
              {contactMethods.map((item) => (
                <div key={item.label} className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-blue-600/10 flex items-center justify-center text-blue-400 flex-shrink-0">
                    <item.icon size={16} />
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">{item.label}</div>
                    <div className="text-sm text-slate-200 font-medium">{item.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            {submitted ? (
              <div className="bento-card p-10 text-center flex flex-col items-center gap-5">
                <div className="w-16 h-16 rounded-2xl bg-blue-600/15 flex items-center justify-center text-blue-400">
                  <Send size={28} />
                </div>
                <h3 className="text-2xl font-bold text-white">Message sent!</h3>
                <p className="text-slate-400 text-sm max-w-xs">
                  Thanks for reaching out. We'll be in your inbox within 24 hours.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="text-xs text-blue-400 hover:text-blue-300 transition-colors mt-2"
                >
                  Send another message →
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bento-card p-6 sm:p-8 flex flex-col gap-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="name" className="text-xs text-slate-500 font-medium">Full Name *</label>
                    <input
                      id="name"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      placeholder="John Smith"
                      className="input-focus px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.08] text-sm text-white placeholder-slate-600 transition-all duration-200"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="email" className="text-xs text-slate-500 font-medium">Work Email *</label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      placeholder="john@company.com"
                      className="input-focus px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.08] text-sm text-white placeholder-slate-600 transition-all duration-200"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="company" className="text-xs text-slate-500 font-medium">Company</label>
                  <input
                    id="company"
                    name="company"
                    value={form.company}
                    onChange={handleChange}
                    placeholder="Acme Inc."
                    className="input-focus px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.08] text-sm text-white placeholder-slate-600 transition-all duration-200"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <span className="text-xs text-slate-500 font-medium">Topic</span>
                  <div className="flex flex-wrap gap-2">
                    {topics.map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setForm((prev) => ({ ...prev, topic: t }))}
                        className={`text-xs px-3 py-1.5 rounded-lg border transition-all duration-200 ${
                          form.topic === t
                            ? 'border-blue-500/60 bg-blue-500/15 text-blue-300'
                            : 'border-white/[0.07] bg-white/[0.02] text-slate-500 hover:text-slate-300 hover:border-white/[0.15]'
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="message" className="text-xs text-slate-500 font-medium">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    placeholder="Tell us about your project, goals, and timeline..."
                    className="input-focus px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.08] text-sm text-white placeholder-slate-600 transition-all duration-200 resize-none"
                  />
                </div>

                <button
                  type="submit"
                  className="btn-glow group flex items-center justify-center gap-2.5 w-full py-3.5 rounded-xl text-sm font-semibold text-white mt-1"
                >
                  Send Message
                  <ArrowRight
                    size={15}
                    className="group-hover:translate-x-1 transition-transform duration-200"
                  />
                </button>

                <p className="text-[11px] text-slate-600 text-center">
                  No spam, ever. First consultation is free.
                </p>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
